import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ArrowDownLeft, CheckCircle2, XCircle, Clock, RefreshCw, ExternalLink, Copy, Search, Radar } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface DepositRequest {
  id: string;
  user_id: string;
  crypto_symbol: string;
  network: string;
  amount: number;
  status: string;
  tx_hash: string | null;
  admin_notes: string | null;
  created_at: string;
  profiles?: { email: string; full_name: string | null; numeric_id: string | null } | null;
  transaction_number?: string | null;
}


const getExplorerUrl = (network: string, txHash: string): string | null => {
  const explorers: Record<string, string> = {
    'ethereum': `https://polygonscan.com/tx/${txHash}`,
    'eth': `https://polygonscan.com/tx/${txHash}`,
    'erc20': `https://polygonscan.com/tx/${txHash}`,
    'bsc': `https://bscscan.com/tx/${txHash}`,
    'bep20': `https://bscscan.com/tx/${txHash}`,
    'polygon': `https://polygonscan.com/tx/${txHash}`,
    'solana': `https://solscan.io/tx/${txHash}`,
    'sol': `https://solscan.io/tx/${txHash}`,
    'tron': `https://tronscan.org/#/transaction/${txHash}`,
    'trc20': `https://tronscan.org/#/transaction/${txHash}`,
    'bitcoin': `https://blockchair.com/bitcoin/transaction/${txHash}`,
    'btc': `https://blockchair.com/bitcoin/transaction/${txHash}`,
    'litecoin': `https://blockchair.com/litecoin/transaction/${txHash}`,
    'ltc': `https://blockchair.com/litecoin/transaction/${txHash}`,
    'avalanche': `https://snowtrace.io/tx/${txHash}`,
    'arbitrum': `https://arbiscan.io/tx/${txHash}`,
    'optimism': `https://optimistic.polygonscan.com/tx/${txHash}`,
    'base': `https://basescan.org/tx/${txHash}`,
    'ton': `https://tonscan.org/tx/${txHash}`,
  };
  return explorers[network.toLowerCase()] || null;
};

const AdminDeposits = () => {
  const { toast } = useToast();
  const [deposits, setDeposits] = useState<DepositRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [searchQuery, setSearchQuery] = useState("");
  const [lookupTxHash, setLookupTxHash] = useState("");
  const [lookupNetwork, setLookupNetwork] = useState("ethereum");

  useEffect(() => { fetchDeposits(); }, []);

  const fetchDeposits = async () => {
    setLoading(true);
    const { data: raw } = await supabase
      .from("deposit_requests")
      .select("*")
      .order("created_at", { ascending: false });

    const rows = (raw || []) as DepositRequest[];
    const userIds = Array.from(new Set(rows.map(r => r.user_id).filter(Boolean)));
    if (userIds.length > 0) {
      const { data: profs } = await supabase
        .from("profiles")
        .select("user_id, email, full_name, numeric_id")
        .in("user_id", userIds);
      const map = new Map((profs || []).map((p: any) => [p.user_id, { email: p.email, full_name: p.full_name, numeric_id: p.numeric_id }]));
      rows.forEach(r => { r.profiles = map.get(r.user_id) || null; });
    }
    setDeposits(rows);
    setLoading(false);
  };

  const handleAction = async (id: string, action: "approved" | "rejected") => {
    setProcessing(id);
    try {
      const deposit = deposits.find(d => d.id === id);
      const { error } = await supabase
        .from("deposit_requests")
        .update({
          status: action,
          admin_notes: notes[id] || null,
          reviewed_at: new Date().toISOString(),
        })
        .eq("id", id);
      if (error) throw error;
      toast({
        title: action === "approved" ? "تمت الموافقة" : "تم الرفض",
        description: action === "approved" ? "تم إضافة الرصيد للمستخدم" : "تم رفض طلب الإيداع",
      });

      // Send email notification
      if (deposit) {
        supabase.functions.invoke("notify-transaction", {
          body: {
            type: "deposit",
            userId: deposit.user_id,
            amount: deposit.amount,
            cryptoSymbol: deposit.crypto_symbol,
            action,
          },
        }).catch(e => console.error("Email notification error:", e));
      }

      fetchDeposits();
    } catch (e: any) {
      toast({ title: "خطأ", description: e.message, variant: "destructive" });
    } finally {
      setProcessing(null);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: "تم النسخ", description: "تم نسخ النص إلى الحافظة" });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending": return <Badge variant="outline" className="text-yellow-500 border-yellow-500"><Clock className="w-3 h-3 ml-1" />قيد المراجعة</Badge>;
      case "approved": return <Badge variant="outline" className="text-green-500 border-green-500"><CheckCircle2 className="w-3 h-3 ml-1" />تمت الموافقة</Badge>;
      case "rejected": return <Badge variant="outline" className="text-red-500 border-red-500"><XCircle className="w-3 h-3 ml-1" />مرفوض</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  const filteredDeposits = deposits.filter(d => {
    const q = searchQuery.toLowerCase();
    if (!q) return true;
    return (
      d.tx_hash?.toLowerCase().includes(q) ||
      d.user_id.toLowerCase().includes(q) ||
      d.crypto_symbol.toLowerCase().includes(q) ||
      d.network.toLowerCase().includes(q) ||
      d.profiles?.email?.toLowerCase().includes(q) ||
      d.profiles?.full_name?.toLowerCase().includes(q)
    );
  });

  if (loading) {
    return <Card className="glass"><CardContent className="p-6"><div className="animate-pulse text-muted-foreground">جاري التحميل...</div></CardContent></Card>;
  }

  return (
    <Card className="glass">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-foreground">
            <ArrowDownLeft className="w-5 h-5 text-primary" />
            طلبات الإيداع
            {deposits.filter(d => d.status === "pending").length > 0 && (
              <Badge className="bg-yellow-500 text-white">{deposits.filter(d => d.status === "pending").length} قيد المراجعة</Badge>
            )}
          </CardTitle>
          <Button variant="outline" size="sm" onClick={fetchDeposits}><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* TX Hash Lookup Tool */}
        <div className="p-4 rounded-lg border border-primary/30 bg-primary/5 space-y-3 mb-4">
          <div className="flex items-center gap-2">
            <Radar className="w-4 h-4 text-primary" />
            <span className="font-semibold text-foreground text-sm">فحص معاملة على البلوكتشين</span>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Select value={lookupNetwork} onValueChange={setLookupNetwork}>
              <SelectTrigger className="sm:w-40 bg-input border-border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ethereum">Ethereum</SelectItem>
                <SelectItem value="bsc">BSC</SelectItem>
                <SelectItem value="tron">Tron</SelectItem>
                <SelectItem value="solana">Solana</SelectItem>
                <SelectItem value="polygon">Polygon</SelectItem>
                <SelectItem value="bitcoin">Bitcoin</SelectItem>
                <SelectItem value="litecoin">Litecoin</SelectItem>
                <SelectItem value="arbitrum">Arbitrum</SelectItem>
                <SelectItem value="optimism">Optimism</SelectItem>
                <SelectItem value="base">Base</SelectItem>
                <SelectItem value="avalanche">Avalanche</SelectItem>
                <SelectItem value="ton">TON</SelectItem>
              </SelectContent>
            </Select>
            <Input
              placeholder="أدخل TX Hash للفحص..."
              value={lookupTxHash}
              onChange={(e) => setLookupTxHash(e.target.value)}
              className="flex-1 bg-input border-border font-mono text-xs"
              dir="ltr"
            />
            <Button
              size="sm"
              disabled={!lookupTxHash.trim()}
              onClick={() => {
                const url = getExplorerUrl(lookupNetwork, lookupTxHash.trim());
                if (url) {
                  window.open(url, '_blank');
                } else {
                  toast({ title: "غير متاح", description: "الشبكة المحددة غير مدعومة", variant: "destructive" });
                }
              }}
              className="shrink-0"
            >
              <ExternalLink className="w-4 h-4 ml-1" />
              فحص
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="بحث بـ TX Hash، مستخدم، عملة..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pr-10 bg-input border-border"
            dir="rtl"
          />
        </div>

        {filteredDeposits.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">لا توجد طلبات إيداع</p>
        ) : (
          filteredDeposits.map(d => (
            <div key={d.id} className={`p-4 rounded-lg border ${d.status === "pending" ? "border-yellow-500/30 bg-yellow-500/5" : "border-border bg-secondary/30"}`}>
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                <div className="space-y-1 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-foreground text-lg">{d.amount} {d.crypto_symbol}</span>
                    {getStatusBadge(d.status)}
                  </div>
                  <p className="text-sm text-muted-foreground">الشبكة: {d.network}</p>
                  {d.transaction_number && (
                    <p className="text-xs text-primary font-mono">#{d.transaction_number}</p>
                  )}
                  {d.profiles && (
                    <p className="text-sm text-foreground">
                      <span className="text-muted-foreground">المستقبل:</span> {d.profiles.full_name || d.profiles.email}{" "}
                      <span className="text-primary font-mono" dir="ltr">(ID: {d.profiles.numeric_id || "-"})</span>
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">المرسل: خارجي (بلوكتشين)</p>
                  
                  
                  {/* TX Hash with explorer link */}
                  {d.tx_hash && (
                    <div className="flex items-center gap-1.5 flex-wrap mt-1 p-2 bg-secondary/50 rounded">
                      <span className="text-xs font-semibold text-muted-foreground">TX Hash:</span>
                      <span className="text-xs text-primary font-mono break-all" dir="ltr">{d.tx_hash}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 shrink-0"
                        onClick={() => copyToClipboard(d.tx_hash!)}
                        title="نسخ"
                      >
                        <Copy className="w-3 h-3 text-muted-foreground" />
                      </Button>
                      {getExplorerUrl(d.network, d.tx_hash) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 gap-1 text-xs text-primary shrink-0"
                          asChild
                        >
                          <a href={getExplorerUrl(d.network, d.tx_hash)!} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="w-3 h-3" />
                            فحص
                          </a>
                        </Button>
                      )}
                    </div>
                  )}

                  {d.admin_notes && <p className="text-xs text-muted-foreground">ملاحظات: {d.admin_notes}</p>}
                  <p className="text-xs text-muted-foreground">
                    {new Date(d.created_at).toLocaleDateString('ar-EG', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>

                {d.status === "pending" && (
                  <div className="space-y-2 shrink-0 sm:w-48">
                    <Input
                      placeholder="ملاحظات (اختياري)"
                      value={notes[d.id] || ""}
                      onChange={e => setNotes(prev => ({ ...prev, [d.id]: e.target.value }))}
                      className="bg-input border-border text-sm"
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                        onClick={() => handleAction(d.id, "approved")}
                        disabled={processing === d.id}
                      >
                        <CheckCircle2 className="w-4 h-4 ml-1" />
                        موافقة
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        className="flex-1"
                        onClick={() => handleAction(d.id, "rejected")}
                        disabled={processing === d.id}
                      >
                        <XCircle className="w-4 h-4 ml-1" />
                        رفض
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
};

export default AdminDeposits;